import { useMutation, useQuery } from 'urql'
import { useNavigate } from 'react-router-dom'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { DEFAULT_APPEARANCE } from 'app/lib/forms/appearance'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'
import {
  CREATE_PAPERWORK_TEMPLATE_MUTATION,
  DELETE_PAPERWORK_TEMPLATE_MUTATION,
  PAPERWORK_TEMPLATES_QUERY,
} from 'app/components/paperwork/graphql'
import type { PaperworkTemplatesQuery } from 'app/generated/gql/graphql'

export type PacketRow = PaperworkTemplatesQuery['paperworkTemplates']['hits'][number]

export const PACKET_EDITOR_PATH = `/settings/forms/packets`

const ALL_PACKETS = { page: 0, limit: 500 }

export type UsePacketsListReturn = {
  packets: PacketRow[]
  creating: boolean
  createPacket: () => Promise<void>
  openPacket: (packet: PacketRow) => void
  deletePacket: (packet: PacketRow) => Promise<void>
}

export function usePacketsList(): UsePacketsListReturn {
  const navigate = useNavigate()
  const [confirm] = useConfirmation()
  const [{ data }] = useQuery({
    query: PAPERWORK_TEMPLATES_QUERY,
    variables: { archived: false, pagination: ALL_PACKETS },
    requestPolicy: `cache-and-network`,
  })
  const [{ fetching: creating }, executeCreate] = useMutation(CREATE_PAPERWORK_TEMPLATE_MUTATION)
  const [, executeDelete] = useMutation(DELETE_PAPERWORK_TEMPLATE_MUTATION)

  const createPacket = async () => {
    const { data: created, error } = await executeCreate({
      input: { name: `New patient paperwork`, forms: [], appearance: DEFAULT_APPEARANCE },
    })
    if (error || !created) {
      notifyError(`Couldn't create the packet`)
      logError(`usePacketsList: create failed`, error?.message)
      return
    }
    navigate(`${PACKET_EDITOR_PATH}/${created.createPaperworkTemplate.id}`)
  }

  const deletePacket = async (packet: PacketRow) => {
    const confirmed = await confirm({
      title: `Delete packet`,
      description: `Delete "${packet.name}"? Links already sent keep working.`,
      variant: `danger`,
    }).then(
      () => true,
      () => false,
    )
    if (!confirmed) return
    const { error } = await executeDelete({ id: packet.id }, { additionalTypenames: [`PaperworkTemplate`] })
    if (error) {
      notifyError(`Couldn't delete the packet`)
      logError(`usePacketsList: delete failed`, error.message)
      return
    }
    notifySuccess(`Packet deleted`)
  }

  return {
    packets: [...(data?.paperworkTemplates.hits ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    creating,
    createPacket,
    openPacket: (packet) => navigate(`${PACKET_EDITOR_PATH}/${packet.id}`),
    deletePacket,
  }
}
